import { io, Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import API_CONFIG from '../config/api';

interface NotificationData {
  title: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  priority: 'high' | 'medium' | 'low';
  category?: string;
  actionUrl?: string;
}

interface MessageData {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  priority: 'high' | 'medium' | 'low';
  category?: string;
  actionUrl?: string;
  createdAt: string;
  isRead: boolean;
}

interface UnreadCountData {
  count: number;
}

interface TestRequestData {
  id: string;
  patientName: string;
  userInfo: string;
  mobile: string;
  createdAt: string;
  status: string;
}

interface NewTestRequestNotification {
  type: 'new_test_request';
  title: string;
  message: string;
  testRequest: TestRequestData;
  timestamp: string;
}

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 6; // fewer attempts overall
  private reconnectInterval = 10000; // slow down to 10s
  private lastToastAt = 0;
  private toastCooldownMs = 60000; // at most one error toast per minute
  private token: string | null = null;
  
  // Event listeners
  private onUnreadCountUpdate: ((count: number) => void) | null = null;
  private onNewMessage: ((message: MessageData) => void) | null = null;
  private onNewNotification: ((notification: NotificationData) => void) | null = null;
  private onNewTestRequest: ((notification: NewTestRequestNotification) => void) | null = null;
  private onNewImageReportRequest: ((notification: any) => void) | null = null;
  private onNewBankPaymentRequest: ((notification: any) => void) | null = null;

  constructor() {
    // Try both possible token keys
    this.token = localStorage.getItem('authToken');

  }

  // Initialize socket connection
  connect() {
    if (this.socket?.connected) {

      return;
    }

    if (!this.token) {

      return;
    }

    try {
      // Use the proxy configured in package.json or explicit URL
      const serverUrl =  API_CONFIG.uploadURL ;

      this.socket = io(serverUrl, {
        transports: ['polling'],
        timeout: 20000,
        forceNew: true,
        upgrade: false,
        autoConnect: true
      });

      this.setupEventListeners();

    } catch (error) {

    }
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;

    }
  }

  // Setup socket event listeners
  private setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {

      this.isConnected = true;
      this.reconnectAttempts = 0;
      
      // Authenticate with token
      if (this.token) {

        this.socket?.emit('authenticate', this.token);
      } else {

      }
    });

    this.socket.on('disconnect', (reason) => {

      this.isConnected = false;
      
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        this.handleReconnect();
      }
    });

    this.socket.on('connect_error', (error) => {

      this.handleReconnect();
    });

    // Authentication events
    this.socket.on('authenticated', (data) => {

      // Request current unread count
      this.socket?.emit('get_unread_count');
    });

    this.socket.on('authentication_error', (data) => {

      toast.error('خطا در اتصال به سرور');
    });

    // Message events
    this.socket.on('new_message', (message: MessageData) => {

      // Show toast notification
      this.showMessageToast(message);
      
      // Call callback if set
      if (this.onNewMessage) {
        this.onNewMessage(message);
      }
    });

    this.socket.on('new_notification', (notification: NotificationData) => {

      // Show toast notification
      this.showNotificationToast(notification);
      
      // Call callback if set
      if (this.onNewNotification) {
        this.onNewNotification(notification);
      }
    });

    this.socket.on('unread_count_update', (data: UnreadCountData) => {

      // Call callback if set
      if (this.onUnreadCountUpdate) {
        this.onUnreadCountUpdate(data.count);
      }
    });

    this.socket.on('system_announcement', (announcement) => {

      toast.info(`📢 ${announcement.message}`);
    });

    // Admin-specific events
    this.socket.on('new_test_request', (notification: NewTestRequestNotification) => {

      // Show toast notification for admins
      toast.info(`🧪 ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Call callback if set
      if (this.onNewTestRequest) {
        this.onNewTestRequest(notification);
      }
    });

    this.socket.on('new_image_report_request', (notification: any) => {

      // Show toast notification for admins
      toast.info(`🖼️ ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Call callback if set
      if (this.onNewImageReportRequest) {
        this.onNewImageReportRequest(notification);
      }
    });

    this.socket.on('new_bank_payment_request', (notification: any) => {

      // Show toast notification for admins
      toast.info(`🏦 ${notification.title}: ${notification.message}`, {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      // Call callback if set
      if (this.onNewBankPaymentRequest) {
        this.onNewBankPaymentRequest(notification);
      }
    });

    this.socket.on('admin_notification', (notification: NotificationData) => {

      // Show toast notification
      this.showNotificationToast(notification);
    });
  }

  // Handle reconnection
  private handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {

      const now = Date.now();
      if (now - this.lastToastAt > this.toastCooldownMs) {
        toast.error('اتصال به سرور قطع شد');
        this.lastToastAt = now;
      }
      return;
    }

    this.reconnectAttempts++;

    const now = Date.now();
    if (now - this.lastToastAt > this.toastCooldownMs) {
      toast.warn('در حال تلاش برای اتصال مجدد...');
      this.lastToastAt = now;
    }
    
    setTimeout(() => {
      if (!this.isConnected) {
        this.connect();
      }
    }, this.reconnectInterval);
  }

  // Show message toast notification
  private showMessageToast(message: MessageData) {
    switch (message.type) {
      case 'success':
        toast.success(message.title);
        break;
      case 'error':
        toast.error(message.title);
        break;
      case 'warning':
        toast.warn(`⚠️ ${message.title}`);
        break;
      default:
        toast.info(`ℹ️ ${message.title}`);
    }
  }

  // Show notification toast
  private showNotificationToast(notification: NotificationData) {
    switch (notification.type) {
      case 'success':
        toast.success(notification.title);
        break;
      case 'error':
        toast.error(notification.title);
        break;
      case 'warning':
        toast.warn(`⚠️ ${notification.title}`);
        break;
      default:
        toast.info(`ℹ️ ${notification.title}`);
    }
  }

  // Set event listeners
  setOnUnreadCountUpdate(callback: (count: number) => void) {
    this.onUnreadCountUpdate = callback;
  }

  setOnNewMessage(callback: (message: MessageData) => void) {
    this.onNewMessage = callback;
  }

  setOnNewNotification(callback: (notification: NotificationData) => void) {
    this.onNewNotification = callback;
  }

  setOnNewTestRequest(callback: (notification: NewTestRequestNotification) => void) {
    this.onNewTestRequest = callback;
  }

  setOnNewImageReportRequest(callback: (notification: any) => void) {
    this.onNewImageReportRequest = callback;
  }

  setOnNewBankPaymentRequest(callback: (notification: any) => void) {
    this.onNewBankPaymentRequest = callback;
  }

  // Emit events
  markMessageAsRead(messageId: string) {
    if (this.socket?.connected) {
      this.socket.emit('message_read', messageId);
    }
  }

  requestUnreadCount() {
    if (this.socket?.connected) {
      this.socket.emit('get_unread_count');
    }
  }

  // Update token and reconnect if needed
  updateToken(newToken: string | null) {
    this.token = newToken;

    if (newToken && !this.isConnected) {
      this.connect();
    } else if (!newToken && this.isConnected) {
      this.disconnect();
    }
  }

  // Check connection status
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  // Get socket instance (for advanced usage)
  getSocket(): Socket | null {
    return this.socket;
  }
}

// Create singleton instance
const socketService = new SocketService();

export default socketService; 